import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyViewUserPlantDailyPowerQuery,
  useLazyViewUserPlantGenerationQuery,
} from "../../../../redux/api/generation/generationAPI";
import {
  selectSolarFilter,
  setSolarFilter,
} from "../../../../redux/features/solar-filter/solar-filter-slice";
import ChartLoadingOverlay from "../../../../shared/components/chart-loading-overlay/chart-loading-overlay";
import IViewUserSolarPlantEnergyResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-energy-response-dto";
import IViewUserSolarPlantPowerResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-power-response-dto";
import { EDateTypes } from "../../../../shared/oversight-core/enums/date-types";
import { EGraphFilters } from "../../../../shared/oversight-core/enums/graph-filters";
import { EUnitType } from "../../../../shared/oversight-core/enums/unit-type";
import { IGoodWeSolarPlantView } from "../../../../shared/oversight-core/interfaces/good-we-solar-plant-view";
import LeafletMapDetails from "../../../../shared/oversight-core/shared-components/leaflet-map-details/leaflet-map-details";
import SolarInfoCard from "../../../../shared/oversight-core/shared-components/solar-info-card/solar-info-card";
import WetherInfo from "../../../../shared/oversight-core/shared-components/wether-info/wether-info";
import AppBannerWithIcon from "../../../../shared/oversight-core/ui-elements/app-banner-with-icon/app-banner-with-icon";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AreaChart from "../../../../shared/oversight-core/ui-elements/area-chart/area-chart";
import ButtonWithBadge from "../../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import { useIsGuest } from "../../../../shared/utils/org-roles";
import GoodWeEnergyFlow from "./energy-flow/good-we-energy-flow";
import GoodWeSiteDashboardFilters from "./site-dashboard-filters/good-we-site-dashboard-filters";
import styles from "./site-dashboard.module.scss";

interface IProps {
  plantId: string;
  solarPlant: IGoodWeSolarPlantView;
}

interface ChartData {
  values: number[];
  labels: string[];
}

const defaultChartDataValues = {
  values: [],
  labels: [],
};

const unitTypes: Option[] = [
  {
    value: EGraphFilters.POWER,
    label: "Solar Power(kW)",
  },
  {
    value: EGraphFilters.ENERGY,
    label: "Solar Energy(kWh)",
  },
  {
    value: EGraphFilters.BATTERY_CHARGED,
    label: "Battery Charged",
  },
  {
    value: EGraphFilters.BATTERY_LEVEL,
    label: "Battery Level",
  },
  {
    value: EGraphFilters.SYSTEM_OVERALL,
    label: "System Overview",
  },
];

const GoodWeSiteDashboard = (props: IProps) => {
  const { plantId, solarPlant } = props;
  // Income is withheld from a monitoring guest; the backend sends none.
  const isGuest = useIsGuest();

  const solarFilterStore = useSelector(selectSolarFilter);
  const dispatch = useDispatch();

  const [selectedUnitType, setSelectedUnitType] = useState<Option>({
    ...unitTypes[0],
  });
  const [powerChartData, setPowerChartData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [energyChartData, setEnergyChartData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [batteryPowerData, setBatteryPowerData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [batteryLevel, setBatteryLevel] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [meterPowerData, setMeterPowerData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [loadPowerData, setLoadPowerData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [showSolarFilterSidePanel, setShowSolarFilterSidePanel] =
    useState(false);
  const [isHideAlerts, setIsHideAlerts] = useState(false);

  const [triggerDailyPlantPower, { isFetching: isFetchingDailyPlantPower }] =
    useLazyViewUserPlantDailyPowerQuery();
  const [triggerDailyPlantEnergy, { isFetching: isFetchingDailyPlantEnergy }] =
    useLazyViewUserPlantGenerationQuery();

  useEffect(() => {
    if (
      selectedUnitType.value !== EGraphFilters.ENERGY &&
      solarFilterStore &&
      plantId
    ) {
      triggerDailyPlantPower({
        plantId: plantId,
        year: new Date(solarFilterStore.selectedDate).getFullYear(),
        month: new Date(solarFilterStore.selectedDate).getMonth() + 1,
        day: new Date(solarFilterStore.selectedDate).getDate(),
      })
        .unwrap()
        .then((res: IViewUserSolarPlantPowerResponseDTO) => {
          const solarPower = res.solarPlantPowerView.solarPower;
          const batteryPower = res.solarPlantPowerView.batterPower;
          const batteryLevel = res.solarPlantPowerView.batterLevel;
          const meterPower = res.solarPlantPowerView.meterPower;
          const loadPower = res.solarPlantPowerView.loadPower;
          setPowerChartData({
            values: solarPower.map((p) => p.power / 1000),
            labels: solarPower.map((p) => moment(p.date).utc().format("HH:mm")),
          });
          setBatteryPowerData({
            values: batteryPower.map((p) => p.power / 1000),
            labels: batteryPower.map((p) =>
              moment(p.date).utc().format("HH:mm"),
            ),
          });
          setBatteryLevel({
            values: batteryLevel.map((b) => b.level),
            labels: batteryLevel.map((b) =>
              moment(b.date).utc().format("HH:mm"),
            ),
          });
          setMeterPowerData({
            values: meterPower.map((p) => p.power / 1000),
            labels: meterPower.map((p) => moment(p.date).utc().format("HH:mm")),
          });
          setLoadPowerData({
            values: loadPower.map((p) => p.power / 1000),
            labels: loadPower.map((p) => moment(p.date).utc().format("HH:mm")),
          });
        })
        .catch(() => {
          setPowerChartData({ ...defaultChartDataValues });
          setBatteryPowerData({ ...defaultChartDataValues });
          setBatteryLevel({ ...defaultChartDataValues });
          setMeterPowerData({ ...defaultChartDataValues });
          setLoadPowerData({ ...defaultChartDataValues });
        });
    }
  }, [
    triggerDailyPlantPower,
    solarFilterStore,
    selectedUnitType.value,
    plantId,
  ]);

  useEffect(() => {
    if (
      selectedUnitType.value === EGraphFilters.ENERGY &&
      solarFilterStore &&
      plantId
    ) {
      triggerDailyPlantEnergy({
        plantId: plantId,
        year: new Date(solarFilterStore.selectedDate).getFullYear(),
        month: new Date(solarFilterStore.selectedDate).getMonth() + 1,
        day: new Date(solarFilterStore.selectedDate).getDate(),
        viewType: solarFilterStore.selectedDateType,
      })
        .unwrap()
        .then((res: IViewUserSolarPlantEnergyResponseDTO) => {
          const solarEnergy = res.solarPlantEnergyView.solarEnergy;
          setEnergyChartData({
            values: solarEnergy.map((e) => e.power / 1000),
            labels: solarEnergy.map((e) =>
              moment(e.date)
                .utcOffset("+05:30")
                .format(
                  solarFilterStore.selectedDateType === EDateTypes.DAILY
                    ? "MM-DD"
                    : solarFilterStore.selectedDateType === EDateTypes.MONTHLY
                      ? "MM-YYYY"
                      : "YYYY",
                ),
            ),
          });
        })
        .catch(() => {
          setEnergyChartData({ ...defaultChartDataValues });
        });
    }
  }, [
    triggerDailyPlantEnergy,
    solarFilterStore,
    selectedUnitType.value,
    plantId,
  ]);

  const onFilter = (selectedDate: Date, selectedDateType: EDateTypes) => {
    dispatch(
      setSolarFilter({
        selectedDate: moment(selectedDate).valueOf(),
        selectedDateType,
      }),
    );
    setShowSolarFilterSidePanel(false);
  };

  const onClearAll = () => {
    dispatch(
      setSolarFilter({
        selectedDate: moment().valueOf(),
        selectedDateType: EDateTypes.DAILY,
      }),
    );
    setShowSolarFilterSidePanel(false);
  };

  const modifiedEnergyChartValues = [...energyChartData.values];
  if (modifiedEnergyChartValues.length > 0) {
    modifiedEnergyChartValues[modifiedEnergyChartValues.length - 1] +=
      solarPlant?.daily_generation ?? 0;
  }

  return (
    <>
      {!isHideAlerts &&
        solarPlant?.solarPlantAlerts &&
        solarPlant?.solarPlantAlerts.length > 0 && (
          <Row className="mb-2">
            <Col>
              <AppBannerWithIcon
                content={
                  <>
                    {solarPlant?.solarPlantAlerts.map((alert, index) => {
                      return (
                        <Row key={index}>
                          <Col className="col-auto text-white font-weight-400 font-size-12 pe-0">
                            {alert.warning_code}&nbsp;&nbsp;&nbsp;|
                          </Col>
                          <Col className="text-white font-weight-400 font-size-12">
                            {alert.warningname}
                          </Col>
                        </Row>
                      );
                    })}
                  </>
                }
                bannerVariant="red"
                icon="emergency_home"
                iconVariant="#FFFFFF"
                iconSize={18}
                button={
                  <>
                    <MaterialIcon
                      icon="close"
                      color="#ffffff"
                      size={15}
                      onClick={() => setIsHideAlerts(true)}
                      className="cursor-pointer"
                    />
                  </>
                }
              />
            </Col>
          </Row>
        )}
      {solarPlant?.serviceError && !isHideAlerts ? (
        <Row className="mb-4">
          <Col>
            <AppBannerWithIcon
              content={
                <Row>
                  <Col className="text-white font-weight-400 font-size-12">
                    API is temporarily down. Showing the last recorded data
                    until live updates resume.
                  </Col>
                </Row>
              }
              bannerVariant="red"
              icon="emergency_home"
              iconVariant="#FFFFFF"
              iconSize={18}
              button={
                <>
                  <MaterialIcon
                    icon="close"
                    color="#ffffff"
                    size={15}
                    onClick={() => setIsHideAlerts(true)}
                    className="cursor-pointer"
                  />
                </>
              }
            />
          </Col>
        </Row>
      ) : (
        <></>
      )}
      <Row>
        <Col className={`${styles.energyFlow} col-12 col-xxl-9`}>
          <GoodWeEnergyFlow solarPlant={solarPlant as IGoodWeSolarPlantView} />
        </Col>
        <Col className="col-xxl-3 mt-4 mt-xxl-0">
          {solarPlant?.address &&
          solarPlant?.latitude &&
          solarPlant?.longitude ? (
            <Row className="mx-0">
              <Col className={`${styles.infoCardContainer} p-3`}>
                {solarPlant?.address && (
                  <Row className="mb-3">
                    <Col className="text-dark font-weight-500 font-size-16">
                      {solarPlant?.address}
                    </Col>
                  </Row>
                )}
                {solarPlant?.latitude && solarPlant?.longitude ? (
                  <>
                    <Row
                      className={`${styles.todayBackground} align-items-center mx-0 p-3`}
                    >
                      <Col>
                        <WetherInfo
                          longitude={solarPlant?.longitude}
                          latitude={solarPlant?.latitude}
                          hideOtherDays
                        />
                      </Col>
                    </Row>
                    {solarPlant?.latitude && solarPlant?.longitude && (
                      <Row className="mt-3">
                        <Col>
                          <LeafletMapDetails
                            locations={[
                              {
                                lat: solarPlant?.latitude,
                                lng: solarPlant?.longitude,
                                name: solarPlant?.plantname,
                              },
                            ]}
                            height={150}
                            hidePopup
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </Col>
            </Row>
          ) : (
            <></>
          )}
        </Col>
      </Row>
      <Row
        className={`${styles.siteStaticContainer} py-4 px-2 mx-0 mt-4 position-relative`}
      >
        <Col>
          <Row className="g-4 row-cols-1 row-cols-sm-2 row-cols-xl-4">
            <Col>
              <SolarInfoCard
                title="Today Generation"
                value={`${
                  Number(
                    Helper.roundTo2(Number(solarPlant?.daily_generation)),
                  ) ?? 0
                } kWh`}
                icon="solar_power"
                iconColor="#D08700"
                backgroundColor="#F7F8EE"
              />
            </Col>
            {!isGuest && (
              <Col>
                <SolarInfoCard
                  title="Today Income"
                  value={`${solarPlant?.daily_income ?? 0} ${
                    solarPlant?.currency ?? ""
                  }`}
                  icon="payments"
                  iconColor="#8EBB13"
                  backgroundColor="#F5F8EE"
                />
              </Col>
            )}
            <Col>
              <SolarInfoCard
                title="Total Generation"
                value={`${solarPlant?.total_generation ?? 0} kWh`}
                icon="solar_power"
                iconColor="#B867B6"
                backgroundColor="#F8F6F8"
              />
            </Col>
            {!isGuest && (
              <Col>
                <SolarInfoCard
                  title="Total Income"
                  value={`${solarPlant?.total_income ?? 0} ${
                    solarPlant?.currency ?? ""
                  }`}
                  icon="payments"
                  iconColor="#26A767"
                  backgroundColor="#F0FAF6"
                />
              </Col>
            )}
          </Row>
          <div className="container-white position-relative mt-4">
            <Row className="align-items-center justify-content-end">
              <Col className="col-auto col-sm-5 col-lg-4 col-xl-3">
                <AppSelect
                  className="mt-1"
                  selectedValue={{ ...selectedUnitType }}
                  options={[...unitTypes]}
                  onChangeOption={(selectedOption) => {
                    setSelectedUnitType(selectedOption);
                  }}
                  id="unit-type"
                />
              </Col>
              <Col className="col-auto ps-1">
                <ButtonWithBadge
                  text="Filter"
                  icon="filter_alt"
                  onClick={() => {
                    setShowSolarFilterSidePanel(true);
                  }}
                  badgeValue={0}
                />
              </Col>
            </Row>
            {selectedUnitType.value === EGraphFilters.SYSTEM_OVERALL ? (
              <AreaChart
                borderColor1="#D08700"
                borderColor2="#8A76D1"
                borderColor3="#3E6E8C"
                borderColor4="#E45151"
                borderColor5="#5E45B8"
                backgroundColor1="#D0870017"
                backgroundColor2="#8A76D117"
                backgroundColor3="#3E6E8C17"
                backgroundColor4="#E4515117"
                backgroundColor5="#5E45B817"
                label1="Solar"
                label2="Battery Power"
                label3="Grid"
                label4="Load"
                label5="Battery Level"
                yAxesUnit="kW"
                yAxesUnitRight="%"
                labels={powerChartData.labels}
                data1={powerChartData.values}
                data2={batteryPowerData.values}
                data3={meterPowerData.values}
                data4={loadPowerData.values}
                data5={batteryLevel.values}
                hasFifthDataSet
                isChartDataAvailable={true}
                xAxisLabel={String(selectedUnitType.label)}
              />
            ) : selectedUnitType.value === EGraphFilters.BATTERY_CHARGED ? (
              <AreaChart
                borderColor1="#8A76D1"
                backgroundColor1="#8A76D117"
                label1="Battery Charged"
                yAxesUnit="kW"
                labels={batteryPowerData.labels}
                data1={batteryPowerData.values}
                isChartDataAvailable={batteryPowerData.values.length > 0}
                xAxisLabel={String(selectedUnitType.label)}
              />
            ) : selectedUnitType.value === EGraphFilters.BATTERY_LEVEL ? (
              <AreaChart
                borderColor1="#5E45B8"
                backgroundColor1="#5E45B817"
                label1="Battery Level"
                yAxesUnit="%"
                labels={batteryLevel.labels}
                data1={batteryLevel.values}
                isChartDataAvailable={batteryLevel.values.length > 0}
                xAxisLabel={String(selectedUnitType.label)}
              />
            ) : (
              <AreaChart
                borderColor1="#D08700"
                backgroundColor1="#D0870017"
                label1={
                  selectedUnitType.value === EGraphFilters.POWER
                    ? "Solar Power"
                    : "Solar Generation"
                }
                yAxesUnit={
                  selectedUnitType.value === EGraphFilters.POWER ? `kW` : `kWh`
                }
                labels={
                  selectedUnitType.value === EGraphFilters.POWER
                    ? powerChartData.labels
                    : energyChartData.labels
                }
                data1={
                  selectedUnitType.value === EGraphFilters.POWER
                    ? powerChartData.values
                    : modifiedEnergyChartValues
                }
                isChartDataAvailable={
                  selectedUnitType.value === EGraphFilters.POWER
                    ? powerChartData.values.length > 0
                    : modifiedEnergyChartValues.length > 0
                }
                xAxisLabel={String(selectedUnitType.label)}
              />
            )}
            <ChartLoadingOverlay
              show={isFetchingDailyPlantPower || isFetchingDailyPlantEnergy}
            />
          </div>
        </Col>
      </Row>
      <GoodWeSiteDashboardFilters
        show={showSolarFilterSidePanel}
        onClose={() => setShowSolarFilterSidePanel(false)}
        onFilter={onFilter}
        onClearAll={onClearAll}
        selectedUnitType={selectedUnitType.value as EUnitType}
      />
    </>
  );
};

export default GoodWeSiteDashboard;
